import React, { useState, useCallback, useEffect } from 'react';
import api from '../api/api';
import WhatChanged, { parseResult } from '../components/sap/WhatChanged';
import VerifyAgainstSap from '../components/sap/VerifyAgainstSap';
import { sapObjectName } from '../components/sap/sapObjectNames';

/**
 * The item codes an event touched, so its row can offer a live check against SAP.
 * A stock document can move several items; each gets its own button because they
 * are verified independently.
 */
function itemsOf(row) {
  const result = parseResult(row);
  if (!result) return [];
  if (result.itemCode) return [result.itemCode];
  const changes = Array.isArray(result.changes) ? result.changes : [];
  return [...new Set(changes.map((c) => c.itemCode))];
}

/**
 * Lets anyone confirm that a change they made in SAP has reached MRP.
 *
 * Deliberately not a second copy of the monitoring screen. That one answers "is the
 * integration healthy" for whoever runs it; this answers the question a user
 * actually has in the moment — "I edited this item a minute ago, did it come
 * through?" — so it is a lookup by record, with a plain yes or no, rather than a
 * feed of events to interpret.
 */

const CARD = 'bg-white border border-gray-200 rounded-lg p-4';
const TH = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 whitespace-nowrap';
const TD = 'px-3 py-2 text-sm text-gray-700 border-t border-gray-100 align-top';

function whenText(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'a moment ago';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

// record_label is the number a person would recognise (an order's DocNum); it is
// only known once the event has been applied, so fall back to the raw SAP key.
function recordOf(row) {
  return row.record_label || row.field_values || '—';
}

// What each queue status means to someone who does not work on the integration.
const PLAIN_STATUS = {
  done: { tone: 'ok', text: 'Received and applied' },
  pending: { tone: 'info', text: 'Received — applying now' },
  processing: { tone: 'info', text: 'Received — applying now' },
  failed: { tone: 'bad', text: 'Received but could not be applied' },
  skipped: { tone: 'none', text: 'Received — this type is not used by MRP' },
};

function Pill({ tone, children }) {
  const tones = {
    ok: 'bg-green-50 text-green-700 border-green-200',
    info: 'bg-blue-50 text-blue-700 border-blue-200',
    bad: 'bg-red-50 text-red-700 border-red-200',
    none: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${tones[tone] || tones.none}`}>
      {children}
    </span>
  );
}

export default function SapUpdateCheck() {
  const [term, setTerm] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(async () => {
    try {
      const res = await api.get('/sap/webhook/status');
      setRecent((res.recent || []).slice(0, 8));
    } catch {
      /* the search below is the point of the page; a quiet failure here is fine */
    }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const search = useCallback(async (e) => {
    if (e) e.preventDefault();
    const q = term.trim();
    if (!q) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await api.get(`/sap/webhook/lookup?q=${encodeURIComponent(q)}`);
      setResult(res);
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setBusy(false);
    }
  }, [term]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-900">Check a SAP Update</h1>
      <p className="text-sm text-gray-500 mt-1 mb-5">
        Changed something in SAP and want to confirm MRP received it? Enter the item code,
        sales order number or document number below.
      </p>

      <form onSubmit={search} className={`${CARD} mb-5`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={term}
            onChange={(ev) => setTerm(ev.target.value)}
            placeholder="e.g. AW201_000_107_00  or  262700797"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sap-blue focus:border-sap-blue outline-none"
          />
          <button
            type="submit"
            disabled={busy || !term.trim()}
            className="px-5 py-2 bg-sap-blue text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Checking…' : 'Check'}
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}

      {result && (
        <div className={`${CARD} mb-5`}>
          {result.found > 0 ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-green-700 text-lg">✓</span>
                <h2 className="text-base font-semibold text-gray-900">
                  Yes — {result.found} update{result.found === 1 ? '' : 's'} received for “{result.term}”
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Most recent {whenText(result.matches[0].received_at)}.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className={TH}>What changed</th>
                      <th className={TH}>Record</th>
                      <th className={TH}>What we saved</th>
                      <th className={TH}>Result</th>
                      <th className={TH}>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.matches.map((m) => {
                      const p = PLAIN_STATUS[m.status] || { tone: 'none', text: m.status };
                      return (
                        <tr key={m.event_id}>
                          <td className={`${TD} font-medium text-gray-900`}>{sapObjectName(m.business_object)}</td>
                          <td className={`${TD} font-mono text-xs`}>{recordOf(m)}</td>
                          <td className={TD}>
                            <WhatChanged row={m} />
                            {itemsOf(m).slice(0, 3).map((code) => (
                              <VerifyAgainstSap key={code} itemCode={code} />
                            ))}
                          </td>
                          <td className={TD}>
                            <Pill tone={p.tone}>{p.text}</Pill>
                            {m.last_error && (
                              <div className="text-xs text-red-600 mt-1 max-w-md">{m.last_error}</div>
                            )}
                          </td>
                          <td className={TD}>
                            {new Date(m.received_at).toLocaleString()}
                            <div className="text-xs text-gray-400">{whenText(m.received_at)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-amber-600 text-lg">—</span>
                <h2 className="text-base font-semibold text-gray-900">
                  No update received for “{result.term}”
                </h2>
              </div>
              <p className="text-sm text-gray-600">
                {result.note || 'Nothing has come through from SAP for this record.'}
              </p>
              <ul className="text-sm text-gray-500 mt-3 list-disc pl-5 space-y-1">
                <li>If the change was made only seconds ago, wait a moment and check again.</li>
                <li>MRP receives updates for items, BOMs and sales orders. Other documents are not sent yet.</li>
                <li>Updates made before live updates were switched on will not appear here.</li>
              </ul>
            </>
          )}
        </div>
      )}

      <div className={CARD}>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Latest updates from SAP</h2>
        {recent.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing received yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={TH}>What changed</th>
                  <th className={TH}>Record</th>
                  <th className={TH}>What we saved</th>
                  <th className={TH}>Result</th>
                  <th className={TH}>When</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => {
                  const p = PLAIN_STATUS[r.status] || { tone: 'none', text: r.status };
                  return (
                    <tr key={r.event_id}>
                      <td className={`${TD} font-medium text-gray-900`}>{sapObjectName(r.business_object)}</td>
                      <td className={`${TD} font-mono text-xs`}>{recordOf(r)}</td>
                      <td className={TD}><WhatChanged row={r} /></td>
                      <td className={TD}><Pill tone={p.tone}>{p.text}</Pill></td>
                      <td className={TD}>{whenText(r.received_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
